/**
 * Dice Component
 *
 * Dynamic dice component that displays all six dice faces (1-6) with football field aesthetics.
 * Features programmatic dot positioning, rolling animations, and optimized SVG structure.
 *
 * Strict MVVM Pattern: This component is completely logic-free.
 * All logic is handled by useDiceViewModel hook.
 *
 * Visual reference: planning/visuals/*.svg - All dice face designs (1-6)
 */

import React from 'react';
import { motion } from 'framer-motion';
import type { DiceProps } from './constants/types';
import { DICE_CONFIG, GRASS_TEXTURE_BASE64, SVG_PATHS } from './utils/utils';
import { useDiceViewModel } from './hooks/useDiceViewModel';
import './Dice.css';

const Dice = React.memo(function Dice({
  value,
  viewBox = DICE_CONFIG.DEFAULT_VIEWBOX,
  className = '',
  isRolling = false,
  animationDuration = DICE_CONFIG.DEFAULT_ANIMATION_DURATION,
  showGrassTexture = DICE_CONFIG.DEFAULT_SHOW_GRASS_TEXTURE,
  onClick,
  disabled = false,
  isClickable = false,
  shouldBounce = false,
  showDiceFace = true,
  showGround = true
}: DiceProps) {
  // ViewModel handles ALL logic - component is just for rendering
  const { state, actions } = useDiceViewModel({
    value,
    className,
    isRolling,
    animationDuration,
    onClick,
    disabled,
    isClickable,
    shouldBounce
  });

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={viewBox}
      className={state.svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      role="img"
      aria-label={`Dice showing ${String(state.validValue)}`}
      aria-live={isRolling ? 'polite' : 'off'}
      focusable="false"
      onClick={actions.handleClick}
      style={{
        cursor: state.cursorStyle
      }}
    >
      <defs>
        {/* Clip path */}
        <clipPath id={state.getId('clip')}>
          <rect width="186.67" height="197.33" fill="white" />
        </clipPath>

        {/* 3D Drop Shadow Filter - creates depth below dice */}
        <filter id={state.getId('dropShadow')} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation={DICE_CONFIG.SHADOW_3D.BLUR_RADIUS} />
          <feOffset
            dx={DICE_CONFIG.SHADOW_3D.OFFSET_X}
            dy={DICE_CONFIG.SHADOW_3D.OFFSET_Y}
            result="offsetblur"
          />
          <feComponentTransfer>
            <feFuncA type="linear" slope={DICE_CONFIG.SHADOW_3D.OPACITY} />
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Inner Shadow/Bevel Filter - creates raised 3D effect on white face */}
        <filter id={state.getId('innerBevel')} x="-50%" y="-50%" width="200%" height="200%">
          {/* Create top-left highlight */}
          <feGaussianBlur
            in="SourceAlpha"
            stdDeviation={DICE_CONFIG.BEVEL.BLUR_RADIUS}
            result="blur"
          />
          <feOffset
            in="blur"
            dx={DICE_CONFIG.BEVEL.HIGHLIGHT_OFFSET}
            dy={DICE_CONFIG.BEVEL.HIGHLIGHT_OFFSET}
            result="offsetBlur"
          />
          <feFlood
            floodColor="white"
            floodOpacity={DICE_CONFIG.BEVEL.HIGHLIGHT_OPACITY}
            result="highlight"
          />
          <feComposite in="highlight" in2="offsetBlur" operator="in" result="topHighlight" />

          {/* Create bottom-right shadow */}
          <feOffset
            in="blur"
            dx={DICE_CONFIG.BEVEL.SHADOW_OFFSET}
            dy={DICE_CONFIG.BEVEL.SHADOW_OFFSET}
            result="offsetBlur2"
          />
          <feFlood
            floodColor="black"
            floodOpacity={DICE_CONFIG.BEVEL.SHADOW_OPACITY}
            result="shadow"
          />
          <feComposite in="shadow" in2="offsetBlur2" operator="in" result="bottomShadow" />

          {/* Merge all layers */}
          <feMerge>
            <feMergeNode in="SourceGraphic" />
            <feMergeNode in="topHighlight" />
            <feMergeNode in="bottomShadow" />
          </feMerge>
        </filter>

        {/* Grass texture pattern (optional) */}
        {showGrassTexture && (
          <pattern
            id={state.getId('pattern')}
            patternContentUnits="objectBoundingBox"
            width="1"
            height="1"
          >
            <use xlinkHref={`#${state.getId('img')}`} transform="scale(0.00436681)" />
          </pattern>
        )}

        {/* Brown gradient base */}
        <linearGradient
          id={state.getId('brownGrad')}
          x1="93.34"
          y1="174.56"
          x2="93.34"
          y2="184.51"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={DICE_CONFIG.GRADIENTS.BROWN_BASE.START} />
          <stop offset="1" stopColor={DICE_CONFIG.GRADIENTS.BROWN_BASE.END} />
        </linearGradient>

        {/* Dark green gradient */}
        <linearGradient
          id={state.getId('darkGreen')}
          x1="93.34"
          y1="174.02"
          x2="93.34"
          y2="166.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={DICE_CONFIG.GRADIENTS.DARK_GREEN.START} />
          <stop offset="1" stopColor={DICE_CONFIG.GRADIENTS.DARK_GREEN.END} />
        </linearGradient>

        {/* Outer green gradient */}
        <linearGradient
          id={state.getId('outerGreen')}
          x1="93.49"
          y1="12.32"
          x2="93.49"
          y2="167.07"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={DICE_CONFIG.GRADIENTS.OUTER_GREEN.START} />
          <stop offset="1" stopColor={DICE_CONFIG.GRADIENTS.OUTER_GREEN.END} />
        </linearGradient>

        {/* Inner green gradient */}
        <linearGradient
          id={state.getId('innerGreen')}
          x1="93.39"
          y1="17.92"
          x2="93.39"
          y2="161.19"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={DICE_CONFIG.GRADIENTS.OUTER_GREEN.START} />
          <stop offset="1" stopColor={DICE_CONFIG.GRADIENTS.OUTER_GREEN.END} />
        </linearGradient>

        {/* White face vertical gradient */}
        <linearGradient
          id={state.getId('whiteVert')}
          x1="93.34"
          y1="25.35"
          x2="93.34"
          y2="153.78"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={DICE_CONFIG.GRADIENTS.WHITE_BORDER.COLOR} />
          <stop
            offset="0.08"
            stopColor={DICE_CONFIG.GRADIENTS.WHITE_BORDER.TRANSPARENT}
            stopOpacity="0"
          />
          <stop
            offset="0.92"
            stopColor={DICE_CONFIG.GRADIENTS.WHITE_BORDER.TRANSPARENT}
            stopOpacity="0"
          />
          <stop offset="1" stopColor={DICE_CONFIG.GRADIENTS.WHITE_BORDER.COLOR} />
        </linearGradient>

        {/* White face horizontal gradient */}
        <linearGradient
          id={state.getId('whiteHoriz')}
          x1="28.81"
          y1="89.84"
          x2="158.12"
          y2="89.84"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={DICE_CONFIG.GRADIENTS.WHITE_BORDER.COLOR} />
          <stop
            offset="0.08"
            stopColor={DICE_CONFIG.GRADIENTS.WHITE_BORDER.TRANSPARENT}
            stopOpacity="0"
          />
          <stop
            offset="0.92"
            stopColor={DICE_CONFIG.GRADIENTS.WHITE_BORDER.TRANSPARENT}
            stopOpacity="0"
          />
          <stop offset="1" stopColor={DICE_CONFIG.GRADIENTS.WHITE_BORDER.COLOR} />
        </linearGradient>

        {/* Radial gradient for 3D dots - creates spherical effect */}
        <radialGradient id={state.getId('dotGradient')}>
          <stop offset="0%" stopColor="#1a1a1a" />
          <stop offset="70%" stopColor="#000000" />
          <stop offset="100%" stopColor="#000000" />
        </radialGradient>

        {/* Grass texture image */}
        {showGrassTexture && (
          <image
            id={state.getId('img')}
            width="229"
            height="229"
            preserveAspectRatio="none"
            xlinkHref={GRASS_TEXTURE_BASE64}
          />
        )}
      </defs>

      {/* Main dice structure with clip path */}
      <g clipPath={`url(#${state.getId('clip')})`}>
        {/* Fixed ground/background (green field) — only rendered when showGround */}
        {showGround && (
          <>
            {/* Brown gradient base layer (bottom shadow) */}
            <path d={SVG_PATHS.BROWN_BASE} fill={`url(#${state.getId('brownGrad')})`} />

            {/* Dark green gradient layer */}
            <path d={SVG_PATHS.DARK_GREEN} fill={`url(#${state.getId('darkGreen')})`} />

            {/* Outer green rounded rectangle */}
            <path d={SVG_PATHS.OUTER_RECT} fill={`url(#${state.getId('outerGreen')})`} />

            {/* Inner rounded rectangle - reuses outerGreen gradient */}
            <path d={SVG_PATHS.INNER_RECT} fill={`url(#${state.getId('outerGreen')})`} />

            {/* Grass texture overlay (optional) */}
            {showGrassTexture && (
              <rect
                x="-20.62"
                y="-24.54"
                width="228"
                height="229"
                fill={`url(#${state.getId('pattern')})`}
              />
            )}
          </>
        )}

        {/* Animated white dice face and dots - only this group rotates with 3D transform */}
        {/* Conditionally render based on showDiceFace prop */}
        {showDiceFace && (
          <motion.g
            animate={state.combinedAnimation}
            className="dice-3d-transform"
            style={{
              transformOrigin: '93.34px 89.56px', // Center of white dice face
              opacity: isRolling ? DICE_CONFIG.INACTIVE_OPACITY : 1
            }}
            filter={state.dropShadowUrl}
          >
            {/* White center face base with 3D bevel effect */}
            <path d={SVG_PATHS.WHITE_FACE} fill="white" filter={state.innerBevelUrl} />

            {/* White face vertical gradient overlay */}
            <path
              d={SVG_PATHS.WHITE_FACE}
              fill={state.whiteVertUrl}
              opacity={DICE_CONFIG.GRADIENT_OVERLAY_OPACITY}
            />

            {/* White face horizontal gradient overlay */}
            <path
              d={SVG_PATHS.WHITE_FACE_HORIZ}
              fill={state.whiteHorizUrl}
              opacity={DICE_CONFIG.GRADIENT_OVERLAY_OPACITY}
            />

            {/* Dynamic dots based on dice value with 3D spherical effect */}
            {state.dotPositions.map((pos, index) => (
              <React.Fragment key={index}>
                {/* Shadow dot (dark gray, offset below for depth) */}
                <circle
                  cx={pos.cx + 0.5}
                  cy={pos.cy + DICE_CONFIG.DOT.SHADOW_OFFSET + 0.5}
                  r={DICE_CONFIG.DOT.RADIUS}
                  fill={DICE_CONFIG.DOT.SHADOW_COLOR}
                  opacity={DICE_CONFIG.DOT.SHADOW_OPACITY}
                />
                {/* Main dot with radial gradient for 3D sphere effect */}
                <circle
                  cx={pos.cx}
                  cy={pos.cy}
                  r={DICE_CONFIG.DOT.RADIUS}
                  fill={state.dotGradientUrl}
                />
                {/* Highlight on dot for glossy 3D effect */}
                <circle
                  cx={pos.cx - state.dotHighlightOffset}
                  cy={pos.cy - state.dotHighlightOffset}
                  r={state.dotHighlightRadius}
                  fill="white"
                  opacity={DICE_CONFIG.DOT.HIGHLIGHT.OPACITY}
                />
              </React.Fragment>
            ))}
          </motion.g>
        )}
      </g>
    </svg>
  );
});

export default Dice;
